<template>
  <div>
    <!-- Header Title & Action -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h4 font-weight-bold text-navy mb-1">
          <v-icon color="primary" class="me-2" large>mdi-chart-areaspline-variant</v-icon>
          تقرير أرباح السائقين
        </h1>
        <div class="text-subtitle-2 text-muted">
          متابعة وتحليل أرباح الرحلات، الحجوزات المكتملة، وصافي دخل الشركة لكافة السائقين
        </div>
      </div>
      <v-btn color="primary" outlined @click="fetchReportData">
        <v-icon left>mdi-refresh</v-icon>
        تحديث البيانات
      </v-btn>
    </div>

    <!-- 1. Top Summary Cards (7 Metrics) -->
    <v-row class="mb-4" v-if="!isLoading && totals">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card elevation-0">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-caption font-weight-bold grey--text">إجمالي أرباح السائقين</span>
              <v-avatar size="36" color="rgba(59, 130, 246, 0.1)">
                <v-icon color="#3B82F6" size="20">mdi-cash-multiple</v-icon>
              </v-avatar>
            </div>
            <div class="text-h5 font-weight-bold primary--text">
              {{ formatMoney(totals.total_driver_earnings) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card elevation-0">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-caption font-weight-bold grey--text">إجمالي دخل الرحلات</span>
              <v-avatar size="36" color="rgba(16, 185, 129, 0.1)">
                <v-icon color="#10B981" size="20">mdi-currency-usd</v-icon>
              </v-avatar>
            </div>
            <div class="text-h5 font-weight-bold success--text">
              {{ formatMoney(totals.gross_trip_revenue) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card elevation-0">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-caption font-weight-bold grey--text">صافي ربح الشركة</span>
              <v-avatar size="36" color="rgba(245, 161, 20, 0.1)">
                <v-icon color="#F5A114" size="20">mdi-chart-line</v-icon>
              </v-avatar>
            </div>
            <div class="text-h5 font-weight-bold warning--text">
              {{ formatMoney(totals.total_company_earnings) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card elevation-0">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-caption font-weight-bold grey--text">رصيد المحافظ الحالية</span>
              <v-avatar size="36" color="rgba(139, 92, 246, 0.1)">
                <v-icon color="#8B5CF6" size="20">mdi-wallet-outline</v-icon>
              </v-avatar>
            </div>
            <div class="text-h5 font-weight-bold purple--text text--darken-1">
              {{ formatMoney(totals.total_pending_driver_balance) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 2. Filters & Controls Card -->
    <v-card class="mb-6 elevation-0 border-card pa-4">
      <v-row align="center">
        <!-- Search Input -->
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filters.search"
            label="ابحث باسم السائق أو رقم الهاتف..."
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            @keyup.enter="fetchReportData"
            @click:clear="onSearchClear"
          ></v-text-field>
        </v-col>

        <!-- Quick Period Select Buttons -->
        <v-col cols="12" md="5" class="d-flex align-center flex-wrap gap-2">
          <v-btn-toggle
            v-model="filters.period"
            mandatory
            color="primary"
            dense
            @change="onPeriodChange"
          >
            <v-btn value="today">اليوم</v-btn>
            <v-btn value="week">هذا الأسبوع</v-btn>
            <v-btn value="month">هذا الشهر</v-btn>
            <v-btn value="custom">فترة مخصصة</v-btn>
            <v-btn value="all">الكل</v-btn>
          </v-btn-toggle>
        </v-col>

        <!-- Search Button -->
        <v-col cols="12" md="3" class="text-end">
          <v-btn color="primary" @click="fetchReportData" :loading="isLoading">
            <v-icon left>mdi-filter-variant</v-icon>
            تطبيق الفلتر
          </v-btn>
        </v-col>

        <!-- Custom Date Range Row -->
        <v-col cols="12" v-if="filters.period === 'custom'" class="pt-2">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="filters.date_from"
                type="date"
                label="من تاريخ"
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="filters.date_to"
                type="date"
                label="إلى تاريخ"
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>

    <!-- 3. Driver Earnings Table Card -->
    <v-card class="elevation-0 border-card">
      <v-data-table
        :headers="headers"
        :items="reportData"
        :loading="isLoading"
        loading-text="جاري تحميل تقرير الأرباح..."
        no-data-text="لا توجد بيانات أرباح متوفرة للفترة المحددة"
        hide-default-footer
        class="elevation-0"
      >
        <!-- Driver Column -->
        <template v-slot:item.driver="{ item }">
          <div class="py-2">
            <div class="font-weight-bold text-subtitle-2">{{ item.driver_name }}</div>
            <div class="text-caption text-muted">
              <v-icon size="14" color="grey">mdi-phone</v-icon>
              {{ item.phone }}
            </div>
          </div>
        </template>

        <!-- Completed Trips -->
        <template v-slot:item.completed_trips_count="{ item }">
          <v-chip color="info" small dark class="font-weight-bold">
            {{ item.completed_trips_count }} رحلة
          </v-chip>
        </template>

        <!-- Gross Revenue -->
        <template v-slot:item.gross_trip_revenue="{ item }">
          <span class="font-weight-bold text-subtitle-2 success--text">
            {{ formatMoney(item.gross_trip_revenue) }}
          </span>
        </template>

        <!-- Driver Earnings -->
        <template v-slot:item.driver_earnings="{ item }">
          <span class="font-weight-bold text-subtitle-2 primary--text">
            {{ formatMoney(item.driver_earnings) }}
          </span>
        </template>

        <!-- Company Net Profit -->
        <template v-slot:item.company_earnings="{ item }">
          <span class="font-weight-bold text-subtitle-2 warning--text">
            {{ formatMoney(item.company_earnings) }}
          </span>
        </template>

        <!-- Paid to Driver -->
        <template v-slot:item.paid_to_driver="{ item }">
          <span>{{ formatMoney(item.paid_to_driver) }}</span>
        </template>

        <!-- Current Wallet Balance -->
        <template v-slot:item.wallet_balance="{ item }">
          <span class="font-weight-bold text-subtitle-2" style="color: #0B1E43;">
            {{ formatMoney(item.wallet_balance) }}
          </span>
        </template>

        <!-- Last Completed Trip -->
        <template v-slot:item.last_completed_trip_at="{ item }">
          <span class="text-caption text-muted">
            {{ item.last_completed_trip_at || 'لا يوجد' }}
          </span>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            color="primary"
            text
            small
            @click="viewDriverDetails(item.driver_id)"
          >
            <v-icon left small>mdi-eye</v-icon>
            عرض التفاصيل
          </v-btn>
        </template>
      </v-data-table>

      <!-- Table Footer / Pagination -->
      <v-divider></v-divider>
      <div class="pa-4 d-flex align-center justify-space-between flex-wrap" v-if="pagination.total > 0">
        <div class="text-caption text-muted">
          عرض الصفحات: إجمالي {{ pagination.total }} سائق
        </div>
        <v-pagination
          v-model="pagination.current_page"
          :length="pagination.last_page"
          :total-visible="7"
          circle
          @input="onPageChange"
        ></v-pagination>
      </div>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "DriverEarningsReport",
  data() {
    return {
      isLoading: false,
      filters: {
        search: "",
        period: "month",
        date_from: "",
        date_to: ""
      },
      totals: null,
      reportData: [],
      pagination: {
        total: 0,
        per_page: 15,
        current_page: 1,
        last_page: 1
      },
      headers: [
        { text: "السائق", value: "driver", sortable: false },
        { text: "الرحلات المكتملة", value: "completed_trips_count" },
        { text: "إجمالي دخل الرحلات", value: "gross_trip_revenue" },
        { text: "أرباح السائق", value: "driver_earnings" },
        { text: "صافي ربح الشركة", value: "company_earnings" },
        { text: "المدفوع للسائق", value: "paid_to_driver" },
        { text: "رصيد المحفظة الحالي", value: "wallet_balance" },
        { text: "آخر رحلة مكتملة", value: "last_completed_trip_at" },
        { text: "الإجراءات", value: "actions", sortable: false }
      ]
    };
  },
  mounted() {
    this.fetchReportData();
  },
  methods: {
    fetchReportData() {
      this.isLoading = true;
      const params = {
        period: this.filters.period,
        search: this.filters.search || "",
        page: this.pagination.current_page,
        per_page: this.pagination.per_page
      };

      if (this.filters.period === "custom") {
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
      }

      axios
        .get("/drivers/earnings-report", { params })
        .then(response => {
          if (response.data && response.data.success) {
            this.totals = response.data.totals;
            this.reportData = response.data.data;
            this.pagination = response.data.pagination;
          }
        })
        .catch(error => {
          this.$notify({
            title: "خطأ",
            text: "فشل تحميل تقرير أرباح السائقين",
            type: "error"
          });
          console.error(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    onPeriodChange() {
      this.pagination.current_page = 1;
      this.fetchReportData();
    },
    onPageChange(page) {
      this.pagination.current_page = page;
      this.fetchReportData();
    },
    onSearchClear() {
      this.filters.search = "";
      this.pagination.current_page = 1;
      this.fetchReportData();
    },
    viewDriverDetails(driverId) {
      this.$router.push(`/users/view/user=${driverId}`);
    },
    formatMoney(val) {
      if (val === null || val === undefined || val === '') {
        return '0.00 EGP';
      }
      const num = parseFloat(val);
      if (isNaN(num)) return '0.00 EGP';
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EGP';
    }
  }
};
</script>

<style lang="scss" scoped>
.text-navy {
  color: #082C69 !important;
}

.border-card {
  border-radius: 12px !important;
  border: 1px solid #E4E8F0 !important;
  background-color: #FFFFFF !important;
}

.stat-card {
  border-radius: 12px !important;
  border: 1px solid #E4E8F0 !important;
  background-color: #FFFFFF !important;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04) !important;
  }
}

.gap-2 {
  gap: 8px;
}
</style>
